import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  getElmUserList as fetchElmUserList,
  postInviteElmUser,
  deleteElmUser as deleteElmUserService,
  putEditElmUser,
  getSupplierDropdownList as fetchSupplierDropdownList,
  getEmailDomainSuppliersList as fetchEmailDomainSuppliersList,
} from "../../../../services/S&OP/BottlerElmServices";
import {
  getDomainList as fetchDomainList,
  getSopRolesByApplicationTypeAndRoleType as fetchSopBottlerRoles,
} from "../../../../services/S&OP/S&OPServices";
import {
  TOAST_FETCH_USER_LIST_ERROR,
  TOAST_INVITE_SUCCESS,
  TOAST_INVITE_ERROR,
  TOAST_DELETE_SUCCESS,
  TOAST_DELETE_ERROR,
  TOAST_UPDATE_SUCCESS,
  TOAST_UPDATE_ERROR,
  TOAST_FETCH_DOMAIN_ERROR,
} from "./constants/bottlerElmAccessText";

const BottlerElmAccessContext = createContext(null);
const ROLE_TYPE_BOTTLER = "BOTTLER";

const flattenStringValues = (value) => {
  if (Array.isArray(value)) return value.flatMap(flattenStringValues);
  if (value && typeof value === "object") {
    return Object.values(value).flatMap(flattenStringValues);
  }
  if (value === undefined || value === null || value === "") return [];
  return [String(value)];
};

const firstPopulatedList = (...values) => {
  for (const value of values) {
    const list = flattenStringValues(value);
    if (list.length) return list;
  }
  return [];
};

const normalizeToken = (value) => String(value || "")
  .trim()
  .toLocaleLowerCase()
  .replace(/[^a-z0-9]+/g, "");

// Same fuzzy match EditBottlerForm uses: a short code (e.g. "US") can be saved
// against a compound label (e.g. "US - United States").
const matchesScopeValue = (configuredValue, savedValue) => {
  const configuredToken = normalizeToken(configuredValue);
  const savedToken = normalizeToken(savedValue);
  if (!configuredToken || !savedToken) return false;
  if (configuredToken === savedToken) return true;
  return configuredToken.startsWith(savedToken) ||
    configuredToken.endsWith(savedToken) ||
    savedToken.startsWith(configuredToken) ||
    savedToken.endsWith(configuredToken);
};

// Region codes must match exactly: fuzzy prefix/suffix matching wrongly pairs
// codes like "EOU" with "EMEOU" or "AOU" with "LAOU".
const matchesRegionValue = (configuredValue, savedValue) => {
  const configuredToken = normalizeToken(configuredValue);
  const savedToken = normalizeToken(savedValue);
  return Boolean(configuredToken) && configuredToken === savedToken;
};

// Grid rows only carry flat region/country/bottler name lists, so the real
// country each bottler belongs to is re-derived from the role's master
// hierarchy (regions -> countries -> bottlers), matching EditBottlerForm's
// resolveInitialEditState. Returns null when no country/bottler can be matched.
const resolveCountryBottlerGroups = (source, roles) => {
  const roleCandidates = [source.role, source.accessLevel, source.roleName, source.roleId].filter(Boolean);
  const regionCandidates = flattenStringValues(source.region);
  const countryCandidates = flattenStringValues(source.countryList);
  const bottlerCandidates = flattenStringValues(source.bottlerGroups);
  if (!countryCandidates.length || !bottlerCandidates.length) return null;

  const matchedRegion = roles
    .flatMap((role) => {
      const roleMatches = !roleCandidates.length || roleCandidates.some((candidate) =>
        matchesScopeValue(role.roleId, candidate) || matchesScopeValue(role.roleName, candidate)
      );
      return roleMatches ? (role.regions || []) : [];
    })
    .find((region) => regionCandidates.some((candidate) =>
      matchesRegionValue(region.regionCode, candidate) || matchesRegionValue(region.regionName, candidate)
    ));
  if (!matchedRegion) return null;

  const groups = {};
  (matchedRegion.countries || []).forEach((country) => {
    const isSelectedCountry = countryCandidates.some((candidate) =>
      matchesScopeValue(country.countryName, candidate) || matchesScopeValue(country.countryCode, candidate)
    );
    if (!isSelectedCountry) return;
    const matchedBottlerNames = (country.bottlers || [])
      .filter((bottler) => bottlerCandidates.some((candidate) =>
        matchesScopeValue(bottler.bottlerName, candidate) || matchesScopeValue(bottler.bottlerId, candidate)
      ))
      .map((bottler) => bottler.bottlerName);
    if (matchedBottlerNames.length) groups[country.countryName] = matchedBottlerNames;
  });

  return Object.keys(groups).length ? groups : null;
};

const normalizeElmUserListRow = (item = {}) => ({
  ...item,
  // Keep legacy keys for current UI flows (edit/delete modal payload construction).
  userEmail: item.userEmail || item.requestedUserEmail || item.requestedFor || "",
  region: item.region || firstPopulatedList(item.regions)[0] || "",
  role: item.role || item.roleName || item.accessLevel || item.roleId || "",
  accessLevel: item.accessLevel || item.roleName || item.role || item.roleId || "",
  countryList: firstPopulatedList(item.countryList, item.countries, item.country),
  bottlerGroups: firstPopulatedList(item.bottlerGroups, item.bottlers, item.bottler),
});

// Maps a grid row ({ requestedUserEmail, region, countryList, bottlerGroups, accessLevel })
// to the bottler write payload contract shared with InviteBottlerForm/EditBottlerForm
// (single "region" string, flat "countries" array).
const toBottlerInvitePayload = (payload, applicationType) => {
  const source = Array.isArray(payload) ? payload[0] || {} : payload || {};
  const region = source.region || "";
  const countries = firstPopulatedList(source.countryList, source.countries, source.country);
  // Prefer an already country-keyed "bottlers" object (see resolveCountryBottlerGroups)
  // over flattening + re-keying by region, which produces the wrong grouping.
  const rawBottlers = source.bottlers;
  const bottlers = rawBottlers && typeof rawBottlers === "object" && !Array.isArray(rawBottlers)
    ? rawBottlers
    : (region ? { [region]: firstPopulatedList(source.bottlerGroups, source.bottler) } : {});

  return {
    role: source.accessLevel || source.role || source.roleName || "",
    region,
    countries,
    bottlers,
    additionalPermissions: {},
    email: localStorage.getItem("userEmail") || "",
    requestedFor: source.requestedUserEmail || "",
    requestedForUserName: source.requestedForUserName || source.userName || "",
    applicationType: source.applicationType || applicationType || "",
  };
};

export const BottlerElmAccessProvider = ({ children }) => {
  const [rowData, setRowData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [domainList, setDomainList] = useState([]);
  const latestUserListRequestRef = useRef(0);
  const loadedApplicationTypeRef = useRef("");

  const getElmUserList = useCallback(async (applicationType, options = {}) => {
    const requestId = ++latestUserListRequestRef.current;

    if (!applicationType) {
      loadedApplicationTypeRef.current = "";
      setRowData([]);
      setTableLoading(false);
      return [];
    }

    const preserveExisting = Boolean(options.preserveExisting) &&
      loadedApplicationTypeRef.current === applicationType;

    try {
      setTableLoading(true);
      if (!preserveExisting) {
        loadedApplicationTypeRef.current = "";
        setRowData([]);
      }

      const response = await fetchElmUserList(applicationType);
      const list = Array.isArray(response) ? response : [];
      const normalizedRows = list
        .filter((item) => item && typeof item === "object")
        .map((item) => normalizeElmUserListRow(item));

      if (requestId !== latestUserListRequestRef.current) return null;

      loadedApplicationTypeRef.current = applicationType;
      setRowData(normalizedRows);
      return normalizedRows;
    } catch {
      if (requestId === latestUserListRequestRef.current) {
        toast.error(TOAST_FETCH_USER_LIST_ERROR);
      }
      return null;
    } finally {
      if (requestId === latestUserListRequestRef.current) {
        setTableLoading(false);
      }
    }
  }, []);

  const inviteUsers = useCallback(async (inviteList, applicationType) => {
    setTableLoading(true);
    try {
      const result = await postInviteElmUser(inviteList);
      toast.success(result.message || TOAST_INVITE_SUCCESS, { autoClose: 2000 });
      await getElmUserList(applicationType);
    } catch (error) {
      const res = error?.response?.data;
      toast.error(res?.errorMessage || TOAST_INVITE_ERROR, { autoClose: 2000 });
    } finally {
        setTableLoading(false);
    }
  }, [getElmUserList]);

  const deleteUser = useCallback(async (payload, applicationType) => {
    setTableLoading(true);
    try {
      const source = Array.isArray(payload) ? payload[0] || {} : payload || {};
      let countryBottlerGroups = null;
      if (flattenStringValues(source.countryList).length) {
        try {
          const rolesResponse = await fetchSopBottlerRoles(applicationType, ROLE_TYPE_BOTTLER);
          const roleList = Array.isArray(rolesResponse)
            ? rolesResponse
            : Array.isArray(rolesResponse?.roles) ? rolesResponse.roles : [];
          countryBottlerGroups = resolveCountryBottlerGroups(source, roleList);
        } catch {
          countryBottlerGroups = null;
        }
      }
      const normalizedPayload = toBottlerInvitePayload(
        countryBottlerGroups ? { ...source, bottlers: countryBottlerGroups } : source,
        applicationType
      );
      const result = await deleteElmUserService(normalizedPayload);
      toast.success(result.message || TOAST_DELETE_SUCCESS, { autoClose: 2000 });
      await getElmUserList(applicationType);
    } catch (error) {
      const res = error?.response?.data;
      toast.error(res?.errorMessage || TOAST_DELETE_ERROR, { autoClose: 2000 });
    } finally {
      setTableLoading(false);
    }
  }, [getElmUserList]);

  const editUser = useCallback(async (payload, applicationType) => {
    setTableLoading(true);
    try {
      const normalizedPayload = toBottlerInvitePayload(payload, applicationType);
      const result = await putEditElmUser(normalizedPayload);
      toast.success(result.message || TOAST_UPDATE_SUCCESS, { autoClose: 2000 });
      await getElmUserList(applicationType);
    } catch (error) {
      const res = error?.response?.data;
      toast.error(res?.errorMessage || TOAST_UPDATE_ERROR, { autoClose: 2000 });
    } finally {
      setTableLoading(false);
    }
  }, [getElmUserList]);

  const getSupplierList = useCallback(async (userEmail) => {
    return await fetchSupplierDropdownList(userEmail);
  }, []);

  const getEmailDomainSuppliers = useCallback(async (email) => {
    return await fetchEmailDomainSuppliersList(email);
  }, []);

  const getDomainList = useCallback(async () => {
    try {
      const list = await fetchDomainList() || [];
      setDomainList(list);
    } catch {
      toast.error(TOAST_FETCH_DOMAIN_ERROR);
    }
  }, []);

  return (
    <BottlerElmAccessContext.Provider
      value={{
        rowData,
        tableLoading,
        domainList,
        getElmUserList,
        inviteUsers,
        deleteUser,
        editUser,
        getSupplierList,
        getEmailDomainSuppliers,
        getDomainList,
      }}
    >
      {children}
    </BottlerElmAccessContext.Provider>
  );
};

export const useBottlerElmAccess = () => {
  const ctx = useContext(BottlerElmAccessContext);
  if (!ctx) throw new Error("useBottlerElmAccess must be used within BottlerElmAccessProvider");
  return ctx;
};
